import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../contexts/AuthContext';

export interface PluginBanner {
  id: string;
  plugin_id: string;
  color: string;
  text: string;
  icon: string;
  enabled: boolean;
  created_at: string;
  // Enhanced customization properties
  banner_position: 'header' | 'footer' | 'left-vertical' | 'right-vertical';
  alignment: 'left' | 'center' | 'right';
  banner_type: 'overlay' | 'card';
  border_style: 'solid' | 'dashed' | 'dotted';
  border_width: number;
  border_radius: number;
  padding: number;
  font_size: number;
  font_weight: 'normal' | 'medium' | 'semibold' | 'bold';
  background_opacity: number;
  animation_enabled: boolean;
  animation_type: 'none' | 'pulse' | 'bounce' | 'fade' | 'slide' | 'glow' | 'shake' | 'rotate' | 'flip';
  animation_duration: number;
  animation_delay: number;
  full_width: boolean;
  margin: number;
  // NEW ENHANCED FEATURES
  priority: number;
  icon_enabled: boolean;
  icon_size: number;
  card_icon_position: 'top-left' | 'top-center' | 'top-right' | 'middle-left' | 'middle-center' | 'middle-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';
  card_icon_placement: 'inside' | 'outside' | 'overlay';
  overlay_text_align: 'left' | 'center' | 'right';
  overlay_icon_position: 'left' | 'right' | 'both' | 'stationary-left' | 'stationary-right' | 'stationary-center';
  // Computed properties for display
  background_color: string;
  text_color: string;
  icon_name: string;
  icon_filter: string;
  border_color: string;
  is_warning?: boolean;
  warning_subtext?: string;
}

// Position options for banner placement
const positionOptions = [
  { value: 'header', label: 'Header (Above Name)', icon: 'arrow-up' },
  { value: 'footer', label: 'Footer (Below Content)', icon: 'arrow-down' },
  { value: 'left-vertical', label: 'Left Vertical', icon: 'arrow-left' },
  { value: 'right-vertical', label: 'Right Vertical', icon: 'arrow-right' }
];

// Alignment options
const alignmentOptions = [
  { value: 'left', label: 'Left', icon: 'align-left' },
  { value: 'center', label: 'Center', icon: 'align-center' },
  { value: 'right', label: 'Right', icon: 'align-right' }
];

// Banner type options
const bannerTypeOptions = [
  { value: 'overlay', label: 'Overlay Banner', icon: 'layout-header', description: 'Traditional banner overlay on the plugin card' },
  { value: 'card', label: 'Card Style', icon: 'card', description: 'Changes the entire plugin card appearance with colored border and overlay' }
];

// Available colors for banners
const availableColors = [
  { name: 'Red', value: '#ef4444' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Yellow', value: '#eab308' },
  { name: 'Green', value: '#22c55e' },
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Purple', value: '#8b5cf6' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Gray', value: '#6b7280' },
  { name: 'White', value: '#ffffff' },
  { name: 'Black', value: '#000000' },
  { name: 'Brown', value: '#8b4513' },
  { name: 'Beige', value: '#f5f5dc' }
];

// Card icon position options
const cardIconPositionOptions = [
  { value: 'top-left', label: 'Top Left', icon: 'arrow-up' },
  { value: 'top-center', label: 'Top Center', icon: 'arrow-up' },
  { value: 'top-right', label: 'Top Right', icon: 'arrow-up' },
  { value: 'middle-left', label: 'Middle Left', icon: 'arrow-left' },
  { value: 'middle-center', label: 'Middle Center', icon: 'plus' },
  { value: 'middle-right', label: 'Middle Right', icon: 'arrow-right' },
  { value: 'bottom-left', label: 'Bottom Left', icon: 'arrow-down' },
  { value: 'bottom-center', label: 'Bottom Center', icon: 'arrow-down' },
  { value: 'bottom-right', label: 'Bottom Right', icon: 'arrow-down' }
];

// Card icon placement options
const cardIconPlacementOptions = [
  { value: 'inside', label: 'Inside Card', icon: 'layout', description: 'Icon appears inside the card boundary' },
  { value: 'outside', label: 'Outside Card', icon: 'external-link', description: 'Icon appears outside the card boundary' },
  { value: 'overlay', label: 'Overlay (Sticker)', icon: 'layers', description: 'Icon overlays both inside and outside' }
];

// Overlay icon position options
const overlayIconPositionOptions = [
  { value: 'left', label: 'Left of Text', icon: 'arrow-left' },
  { value: 'right', label: 'Right of Text', icon: 'arrow-right' },
  { value: 'both', label: 'Both Sides', icon: 'arrows-horizontal' },
  { value: 'stationary-left', label: 'Stationary Left', icon: 'layout-align-left' },
  { value: 'stationary-right', label: 'Stationary Right', icon: 'layout-align-right' },
  { value: 'stationary-center', label: 'Stationary Center', icon: 'layout-align-center' }
];

const animationOptions = [
  { value: 'none', label: 'None' },
  { value: 'pulse', label: 'Pulse' },
  { value: 'bounce', label: 'Bounce' },
  { value: 'fade', label: 'Fade' },
  { value: 'slide', label: 'Slide' },
  { value: 'glow', label: 'Glow' },
  { value: 'shake', label: 'Shake' },
  { value: 'rotate', label: 'Rotate' },
  { value: 'flip', label: 'Flip' }
];

export const PluginBannerManager: React.FC = () => {
  const { isAdmin } = useAuth();
  const [banners, setBanners] = useState<PluginBanner[]>([]);
  const [plugins, setPlugins] = useState<{ id: string; name: string }[]>([]);
  const [pluginsLoading, setPluginsLoading] = useState(true);
  const [selectedPlugin, setSelectedPlugin] = useState('');
  const [bannerForm, setBannerForm] = useState({
    color: '#3b82f6',
    text: '',
    icon: 'info-circle',
    banner_position: 'header' as 'header' | 'footer' | 'left-vertical' | 'right-vertical',
    alignment: 'left' as 'left' | 'center' | 'right',
    banner_type: 'overlay' as 'overlay' | 'card',
    border_style: 'dashed' as 'solid' | 'dashed' | 'dotted',
    border_width: 2,
    border_radius: 8,
    padding: 12,
    font_size: 14,
    font_weight: 'semibold' as 'normal' | 'medium' | 'semibold' | 'bold',
    background_opacity: 0.15,
    animation_enabled: false,
    animation_type: 'none' as 'none' | 'pulse' | 'bounce' | 'fade' | 'slide' | 'glow' | 'shake' | 'rotate' | 'flip',
    animation_duration: 2000,
    animation_delay: 0,
    full_width: false,
    margin: 8,
    // NEW ENHANCED FEATURES
    priority: 0,
    icon_enabled: true,
    icon_size: 16,
    card_icon_position: 'top-left' as 'top-left' | 'top-center' | 'top-right' | 'middle-left' | 'middle-center' | 'middle-right' | 'bottom-left' | 'bottom-center' | 'bottom-right',
    card_icon_placement: 'overlay' as 'inside' | 'outside' | 'overlay',
    overlay_text_align: 'center' as 'left' | 'center' | 'right',
    overlay_icon_position: 'left' as 'left' | 'right' | 'both' | 'stationary-left' | 'stationary-right' | 'stationary-center'
  });
  const [editingBanner, setEditingBanner] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<'basic' | 'style' | 'animation' | 'banner_position' | 'advanced'>('basic');

  // Available icon options with search functionality
  const [iconSearch, setIconSearch] = useState('');
  const iconOptions = [
    '4g', '4k-box', '4k', '5g', 'ab-testing', 'ac', 'add-box-multiple', 'add-box', 'add-col', 'add-grid',
    'add-row', 'alert', 'align-center', 'align-justify', 'align-left', 'align-right', 'analytics', 'anchor',
    'android', 'animation', 'archive', 'arrow-bar-down', 'arrow-bar-left', 'arrow-bar-right', 'arrow-bar-up',
    'arrow-down-box', 'arrow-down', 'arrow-left-box', 'arrow-left', 'arrow-right-box', 'arrow-right',
    'arrow-up-box', 'arrow-up', 'arrows-horizontal', 'arrows-vertical', 'art-text', 'article-multiple',
    'article', 'aspect-ratio', 'at', 'attachment', 'audio-device', 'avatar', 'backburger', 'battery-1',
    'battery-2', 'battery-charging', 'battery-full', 'battery', 'bed', 'bitcoin', 'bluetooth', 'book-open',
    'book', 'bookmark', 'bookmarks', 'briefcase-account', 'briefcase-check', 'briefcase-delete',
    'briefcase-download', 'briefcase-minus', 'briefcase-plus', 'briefcase-search-1', 'briefcase-search',
    'briefcase-upload', 'briefcase', 'bug', 'building-community', 'building-skyscraper', 'building',
    'buildings', 'bulletlist', 'bullseye-arrow', 'bullseye', 'bus', 'cake', 'calculator', 'calendar-alert',
    'calendar-arrow-left', 'calendar-arrow-right', 'calendar-check', 'calendar-export', 'calendar-grid',
    'calendar-import', 'calendar-minus', 'calendar-month', 'calendar-multiple-check', 'calendar-multiple',
    'calendar-plus', 'calendar-range', 'calendar-remove', 'calendar-search', 'calendar-sort-ascending',
    'calendar-sort-descending', 'calendar-text', 'calendar-today', 'calendar-tomorrow', 'calendar-week-begin',
    'calendar-week', 'calendar-weekend', 'calendar', 'camera-add', 'camera-alt', 'camera-face', 'camera',
    'car', 'card-id', 'card-plus', 'card-stack', 'card-text', 'card', 'cart', 'cast', 'cellular-signal-0',
    'cellular-signal-1', 'cellular-signal-2', 'cellular-signal-3', 'cellular-signal-off', 'chart-add',
    'chart-bar', 'chart-delete', 'chart-minus', 'chart-multiple', 'chart', 'chat', 'check-double', 'check',
    'checkbox-on', 'checkbox', 'checklist', 'chess', 'chevron-down', 'chevron-left', 'chevron-right',
    'chevron-up', 'chevrons-horizontal', 'chevrons-vertical', 'circle', 'clipboard', 'clock', 'close-box',
    'close', 'cloud-done', 'cloud-download', 'cloud-moon', 'cloud-sun', 'cloud-upload', 'cloud', 'cocktail',
    'code', 'coffee-alt', 'coffee', 'coin', 'collapse', 'colors-swatch', 'command', 'comment', 'contact-delete',
    'contact-multiple', 'contact-plus', 'contact', 'copy', 'corner-down-left', 'corner-down-right',
    'corner-left-down', 'corner-left-up', 'corner-right-down', 'corner-right-up', 'corner-up-left',
    'corner-up-right', 'credit-card-delete', 'credit-card-minus', 'credit-card-multiple', 'credit-card-plus',
    'credit-card-settings', 'credit-card-wireless', 'credit-card', 'crop', 'cut', 'dashboard', 'debug-check',
    'debug-off', 'debug-pause', 'debug-play', 'debug-stop', 'debug', 'delete', 'deskphone', 'device-laptop',
    'device-phone', 'device-tablet', 'device-tv-smart', 'device-tv', 'device-vibrate', 'device-watch',
    'devices', 'dice', 'dollar', 'downasaur', 'download', 'draft', 'drag-and-drop', 'drop-area', 'drop-full',
    'drop-half', 'drop', 'duplicate-alt', 'duplicate', 'edit-box', 'edit', 'euro', 'expand', 'external-link',
    'eye-closed', 'eye', 'file-alt', 'file-delete', 'file-flash', 'file-minus', 'file-multiple', 'file-off',
    'file-plus', 'file', 'fill-half', 'fill', 'flag', 'flatten', 'flip-to-back', 'flip-to-front', 'float-center',
    'float-left', 'float-right', 'folder-minus', 'folder-plus', 'folder-x', 'folder', 'forward', 'forwardburger',
    'frame-add', 'frame-check', 'frame-delete', 'frame-minus', 'frame', 'gamepad', 'gif', 'gift', 'git-branch',
    'git-commit', 'git-merge', 'git-pull-request', 'github-2', 'github', 'gps', 'grid', 'group', 'hd',
    'headphone', 'headset', 'heart', 'hidden', 'home', 'hourglass', 'hq', 'human-handsdown', 'human-handsup',
    'human-height-alt', 'human-height', 'human-run', 'human', 'image-arrow-right', 'image-broken', 'image-delete',
    'image-flash', 'image-frame', 'image-gallery', 'image-multiple', 'image-new', 'image-plus', 'image',
    'inbox-all', 'inbox-full', 'inbox', 'info-box', 'invert', 'iso', 'kanban', 'keyboard', 'label-alt-multiple',
    'label-alt', 'label-sharp', 'label', 'layout-align-bottom', 'layout-align-left', 'layout-align-right',
    'layout-align-top', 'layout-columns', 'layout-distribute-horizontal', 'layout-distribute-vertical',
    'layout-footer', 'layout-header', 'layout-rows', 'layout-sidebar-left', 'layout-sidebar-right', 'layout',
    'lightbulb-2', 'lightbulb-on', 'lightbulb', 'link', 'list-box', 'list', 'loader', 'lock-open', 'lock',
    'login', 'logout', 'luggage', 'mail-arrow-right', 'mail-check', 'mail-delete', 'mail-flash', 'mail-multiple',
    'mail-off', 'mail-unread', 'mail', 'map', 'mastodon', 'membercard', 'menu', 'message-arrow-left',
    'message-arrow-right', 'message-bookmark', 'message-clock', 'message-delete', 'message-flash',
    'message-image', 'message-minus', 'message-plus', 'message-processing', 'message-reply', 'message-text',
    'message', 'minus', 'missed-call', 'modem', 'money', 'monitor', 'mood-happy', 'mood-neutral', 'mood-sad',
    'moon-star', 'moon-stars', 'moon', 'more-horizontal', 'more-vertical', 'mouse', 'move', 'movie', 'music',
    'next', 'note-delete', 'note-multiple', 'note-plus', 'note', 'notes-delete', 'notes-multiple', 'notes-plus',
    'notes', 'notification-off', 'notification', 'open', 'paint-bucket', 'paperclip', 'pause', 'percent',
    'picture-in-picture-alt', 'picture-in-picture', 'pin', 'pixelarticons', 'play', 'playlist', 'plus', 'power',
    'prev', 'print', 'radio-handheld', 'radio-on', 'radio-signal', 'radio-tower', 'reciept-alt', 'reciept',
    'redo', 'reload', 'remove-box-multiple', 'remove-box', 'repeat', 'reply-all', 'reply', 'rounded-corner',
    'save', 'scale', 'script-text', 'script', 'scroll-horizontal', 'scroll-vertical', 'sd', 'search',
    'section-copy', 'section-minus', 'section-plus', 'section-x', 'section', 'server', 'sharp-corner',
    'shield-off', 'shield', 'ship', 'shopping-bag', 'shuffle', 'sliders-2', 'sliders', 'sort-alphabetic',
    'sort-numeric', 'sort', 'speaker', 'speed-fast', 'speed-medium', 'speed-slow', 'spotlight', 'store',
    'subscriptions', 'subtitles', 'suitcase', 'sun-alt', 'sun', 'switch', 'sync', 'tab', 'table', 'tea',
    'teach', 'text-add', 'text-colums', 'text-search', 'text-wrap', 'timeline', 'toggle-left', 'toggle-right',
    'tournament', 'track-changes', 'trash-alt', 'trash', 'trending-down', 'trending-up', 'trending', 'trophy',
    'truck', 'undo', 'ungroup', 'unlink', 'upload', 'user-minus', 'user-plus', 'user-x', 'user', 'users',
    'video-off', 'video', 'view-col', 'view-list', 'viewport-narrow', 'viewport-wide', 'visible', 'volume-1',
    'volume-2', 'volume-3', 'volume-minus', 'volume-plus', 'volume-vibrate', 'volume-x', 'volume', 'wallet',
    'warning-box', 'wind', 'zap', 'zoom-in', 'zoom-out'
  ];

  // Filter icons based on search
  const filteredIcons = iconOptions.filter(icon => 
    icon.toLowerCase().includes(iconSearch.toLowerCase())
  );

  useEffect(() => {
    if (isAdmin) {
      loadPluginsFromDatabase();
      loadBanners();
    }
  }, [isAdmin]);

  const loadPluginsFromDatabase = async () => {
    try {
      setPluginsLoading(true);
      const { data, error } = await supabase
        .from('plugins')
        .select('id, name')
        .eq('enabled', true)
        .order('name');

      if (error) {
        console.error('Error loading plugins:', error);
        setPlugins([]);
      } else {
        setPlugins(data || []);
      }
    } catch (err) {
      console.error('Error loading plugins from database:', err);
      setPlugins([]);
    } finally {
      setPluginsLoading(false);
    }
  };

  const loadBanners = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('plugin_banners')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setBanners(data || []);
    } catch (error) {
      console.error('Error loading banners:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBanner = async () => {
    if (!selectedPlugin || !bannerForm.text.trim() || submitting) return;

    try {
      setSubmitting(true);
      const { data, error } = await supabase
        .from('plugin_banners')
        .insert({
          plugin_id: selectedPlugin,
          ...bannerForm,
          text: bannerForm.text.trim(),
          enabled: true
        })
        .select()
        .single();

      if (error) throw error;

      // Refresh banners
      await loadBanners();
      
      // Reset form
      setSelectedPlugin('');
      setBannerForm({
        color: '#3b82f6',
        text: '',
        icon: 'info-circle',
        banner_position: 'header' as 'header' | 'footer' | 'left-vertical' | 'right-vertical',
        alignment: 'left' as 'left' | 'center' | 'right',
        border_style: 'dashed' as 'solid' | 'dashed' | 'dotted',
        border_width: 2,
        border_radius: 8,
        padding: 12,
        font_size: 14,
        font_weight: 'semibold' as 'normal' | 'medium' | 'semibold' | 'bold',
        background_opacity: 0.15,
        animation_enabled: false,
        animation_type: 'none' as 'none' | 'pulse' | 'bounce' | 'fade' | 'slide' | 'glow' | 'shake' | 'rotate' | 'flip',
        animation_duration: 2000,
        animation_delay: 0,
        full_width: false,
        margin: 8,
        banner_type: 'overlay' as 'overlay' | 'card',
        // NEW ENHANCED FEATURES
        priority: 1,
        icon_enabled: true,
        icon_size: 16,
        card_icon_position: 'top-left' as 'top-left' | 'top-center' | 'top-right' | 'middle-left' | 'middle-center' | 'middle-right' | 'bottom-left' | 'bottom-center' | 'bottom-right',
        card_icon_placement: 'overlay' as 'inside' | 'outside' | 'overlay',
        overlay_text_align: 'left' as 'left' | 'center' | 'right',
        overlay_icon_position: 'left' as 'left' | 'right' | 'both' | 'stationary-left' | 'stationary-right' | 'stationary-center'
      });
    } catch (error) {
      console.error('Error creating banner:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateBanner = async () => {
    if (!editingBanner || !bannerForm.text.trim() || submitting) return;

    try {
      setSubmitting(true);
      const { error } = await supabase
        .from('plugin_banners')
        .update({
          ...bannerForm,
          text: bannerForm.text.trim()
        })
        .eq('id', editingBanner);

      if (error) throw error;

      await loadBanners();
      setEditingBanner(null);
      setBannerForm({
        color: '#3b82f6',
        text: '',
        icon: 'info-circle',
        banner_position: 'header' as 'header' | 'footer' | 'left-vertical' | 'right-vertical',
        alignment: 'left' as 'left' | 'center' | 'right',
        border_style: 'dashed' as 'solid' | 'dashed' | 'dotted',
        border_width: 2,
        border_radius: 8,
        padding: 12,
        font_size: 14,
        font_weight: 'semibold' as 'normal' | 'medium' | 'semibold' | 'bold',
        background_opacity: 0.15,
        animation_enabled: false,
        animation_type: 'none' as 'none' | 'pulse' | 'bounce' | 'fade' | 'slide' | 'glow' | 'shake' | 'rotate' | 'flip',
        animation_duration: 2000,
        animation_delay: 0,
        full_width: false,
        margin: 8,
        banner_type: 'overlay' as 'overlay' | 'card',
        // NEW ENHANCED FEATURES
        priority: 1,
        icon_enabled: true,
        icon_size: 16,
        card_icon_position: 'top-left' as 'top-left' | 'top-center' | 'top-right' | 'middle-left' | 'middle-center' | 'middle-right' | 'bottom-left' | 'bottom-center' | 'bottom-right',
        card_icon_placement: 'inside' as 'inside' | 'outside' | 'overlay',
        overlay_text_align: 'left' as 'left' | 'center' | 'right',
        overlay_icon_position: 'left' as 'left' | 'right' | 'both' | 'stationary-left' | 'stationary-right' | 'stationary-center'
      });
    } catch (error) {
      console.error('Error updating banner:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditBanner = (banner: PluginBanner) => {
    setEditingBanner(banner.id);
    setBannerForm({
      color: banner.color,
      text: banner.text,
      icon: banner.icon,
      banner_position: banner.banner_position || 'header' as 'header' | 'footer' | 'left-vertical' | 'right-vertical',
      alignment: banner.alignment || 'left' as 'left' | 'center' | 'right',
      border_style: banner.border_style || 'dashed' as 'solid' | 'dashed' | 'dotted',
      border_width: banner.border_width || 2,
      border_radius: banner.border_radius || 8,
      padding: banner.padding || 12,
      font_size: banner.font_size || 14,
      font_weight: banner.font_weight || 'semibold' as 'normal' | 'medium' | 'semibold' | 'bold',
      background_opacity: banner.background_opacity || 0.15,
      animation_enabled: banner.animation_enabled || false,
      animation_type: banner.animation_type || 'none' as 'none' | 'pulse' | 'bounce' | 'fade' | 'slide' | 'glow' | 'shake' | 'rotate' | 'flip',
      animation_duration: banner.animation_duration || 2000,
      animation_delay: banner.animation_delay || 0,
      full_width: banner.full_width || false,
      margin: banner.margin || 8,
      banner_type: banner.banner_type || 'overlay' as 'overlay' | 'card',
      // NEW ENHANCED FEATURES
      priority: banner.priority || 1,
      icon_enabled: banner.icon_enabled || true,
      icon_size: banner.icon_size || 16,
      card_icon_position: banner.card_icon_position || 'top-left' as 'top-left' | 'top-center' | 'top-right' | 'middle-left' | 'middle-center' | 'middle-right' | 'bottom-left' | 'bottom-center' | 'bottom-right',
      card_icon_placement: banner.card_icon_placement || 'inside' as 'inside' | 'outside' | 'overlay',
      overlay_text_align: banner.overlay_text_align || 'left' as 'left' | 'center' | 'right',
      overlay_icon_position: banner.overlay_icon_position || 'left' as 'left' | 'right' | 'both' | 'stationary-left' | 'stationary-right' | 'stationary-center'
    });
  };

  const handleDeleteBanner = async (bannerId: string) => {
    try {
      const { error } = await supabase
        .from('plugin_banners')
        .delete()
        .eq('id', bannerId);

      if (error) throw error;
      await loadBanners();
    } catch (error) {
      console.error('Error deleting banner:', error);
    }
  };

  const handleToggleBanner = async (bannerId: string) => {
    try {
      const banner = banners.find(b => b.id === bannerId);
      if (!banner) return;

      const { error } = await supabase
        .from('plugin_banners')
        .update({ enabled: !banner.enabled })
        .eq('id', bannerId);

      if (error) throw error;
      await loadBanners();
    } catch (error) {
      console.error('Error toggling banner:', error);
    }
  };

  // Get preview styles for live preview
  const getPreviewStyles = () => {
    const animationClass = bannerForm.animation_enabled ? getAnimationClass(bannerForm.animation_type, bannerForm.animation_duration) : '';
    
    return {
      backgroundColor: hexToRgba(bannerForm.color, bannerForm.background_opacity),
      borderColor: bannerForm.color,
      color: bannerForm.color,
      borderStyle: bannerForm.border_style,
      borderWidth: `${bannerForm.border_width}px`,
      borderRadius: `${bannerForm.border_radius}px`,
      padding: `${bannerForm.padding}px`,
      fontSize: `${bannerForm.font_size}px`,
      fontWeight: bannerForm.font_weight,
      margin: `${bannerForm.margin}px`,
      width: bannerForm.full_width ? '100%' : 'auto',
      animationDuration: bannerForm.animation_enabled ? `${bannerForm.animation_duration}ms` : undefined,
      animationDelay: bannerForm.animation_enabled ? `${bannerForm.animation_delay}ms` : undefined,
      className: animationClass
    };
  };

  // Helper function to convert hex color to rgba with opacity
  const hexToRgba = (hex: string, alpha: number): string => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Animation class mapping
  const getAnimationClass = (type: string, duration: number): string => {
    const baseClasses: { [key: string]: string } = {
      'none': '',
      'pulse': 'animate-pulse',
      'bounce': 'animate-bounce',
      'fade': 'animate-pulse',
      'slide': 'animate-pulse',
      'glow': 'animate-ping',
      'shake': 'animate-bounce',
      'rotate': 'animate-spin',
      'flip': 'animate-pulse'
    };
    
    return baseClasses[type] || '';
  };

  // Get color filter for icon color matching
  const getColorFilter = (color: string): string => {
    const hex = color.replace('#', '');
    
    const colorFilters: { [key: string]: string } = {
      'ef4444': 'brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(346deg) brightness(104%) contrast(97%)',
      'dc2626': 'brightness(0) saturate(100%) invert(22%) sepia(77%) saturate(6236%) hue-rotate(354deg) brightness(91%) contrast(94%)',
      'f97316': 'brightness(0) saturate(100%) invert(58%) sepia(79%) saturate(2476%) hue-rotate(24deg) brightness(95%) contrast(105%)',
      'ea580c': 'brightness(0) saturate(100%) invert(48%) sepia(96%) saturate(4161%) hue-rotate(14deg) brightness(96%) contrast(96%)',
      'eab308': 'brightness(0) saturate(100%) invert(85%) sepia(50%) saturate(586%) hue-rotate(1deg) brightness(104%) contrast(107%)',
      'ca8a04': 'brightness(0) saturate(100%) invert(65%) sepia(84%) saturate(1178%) hue-rotate(6deg) brightness(91%) contrast(96%)',
      '22c55e': 'brightness(0) saturate(100%) invert(48%) sepia(79%) saturate(2476%) hue-rotate(86deg) brightness(118%) contrast(119%)',
      '16a34a': 'brightness(0) saturate(100%) invert(39%) sepia(84%) saturate(1783%) hue-rotate(86deg) brightness(96%) contrast(90%)',
      '3b82f6': 'brightness(0) saturate(100%) invert(40%) sepia(93%) saturate(1352%) hue-rotate(214deg) brightness(100%) contrast(97%)',
      '2563eb': 'brightness(0) saturate(100%) invert(29%) sepia(94%) saturate(7463%) hue-rotate(213deg) brightness(93%) contrast(94%)',
      '8b5cf6': 'brightness(0) saturate(100%) invert(55%) sepia(88%) saturate(3200%) hue-rotate(257deg) brightness(97%) contrast(93%)',
      '7c3aed': 'brightness(0) saturate(100%) invert(29%) sepia(89%) saturate(7048%) hue-rotate(268deg) brightness(90%) contrast(96%)',
      'ec4899': 'brightness(0) saturate(100%) invert(47%) sepia(74%) saturate(6187%) hue-rotate(301deg) brightness(90%) contrast(89%)',
      'd946ef': 'brightness(0) saturate(100%) invert(64%) sepia(18%) saturate(3258%) hue-rotate(287deg) brightness(105%) contrast(97%)',
      '6b7280': 'brightness(0) saturate(100%) invert(60%) sepia(8%) saturate(878%) hue-rotate(157deg) brightness(90%) contrast(87%)',
      '4b5563': 'brightness(0) saturate(100%) invert(37%) sepia(15%) saturate(709%) hue-rotate(169deg) brightness(89%) contrast(86%)',
      'ffffff': 'brightness(0) saturate(100%) invert(100%) sepia(0%) saturate(700%) hue-rotate(0deg) brightness(100%) contrast(100%)',
      '000000': 'brightness(0) saturate(100%) invert(0%) sepia(100%) saturate(700%) hue-rotate(0deg) brightness(100%) contrast(100%)',
      '8b4513': 'brightness(0) saturate(100%) invert(20%) sepia(70%) saturate(1000%) hue-rotate(30deg) brightness(90%) contrast(90%)',
      'f5f5dc': 'brightness(0) saturate(100%) invert(90%) sepia(10%) saturate(100%) hue-rotate(60deg) brightness(100%) contrast(100%)'
    };
    
    return colorFilters[hex.toLowerCase()] || 'brightness(0) saturate(100%) invert(50%) sepia(100%) saturate(200%) hue-rotate(0deg)';
  };

  // Helper to get class for card icon position
  const getCardIconPositionClass = (position: string, placement: string): string => {
    let classes = '';
    if (position === 'top-left') {
      classes = 'top-0 left-0';
    } else if (position === 'top-center') {
      classes = 'top-0 left-1/2 -translate-x-1/2';
    } else if (position === 'top-right') {
      classes = 'top-0 right-0';
    } else if (position === 'middle-left') {
      classes = 'top-1/2 -translate-y-1/2 left-0';
    } else if (position === 'middle-center') {
      classes = 'top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2';
    } else if (position === 'middle-right') {
      classes = 'top-1/2 -translate-y-1/2 right-0';
    } else if (position === 'bottom-left') {
      classes = 'bottom-0 left-0';
    } else if (position === 'bottom-center') {
      classes = 'bottom-0 left-1/2 -translate-x-1/2';
    } else if (position === 'bottom-right') {
      classes = 'bottom-0 right-0';
    }

    if (placement === 'overlay') {
      classes += ' absolute';
    } else if (placement === 'outside') {
      classes += ' relative';
    }

    return classes;
  };

  if (!isAdmin) {
    return (
      <div className="p-6">
        <div className="bg-red-100 border border-red-300 rounded-lg p-4 text-red-700">
          Access denied. Admin privileges required.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-primary"></div>
          <span className="ml-2 text-theme-text">Loading banners...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-theme-surface border-2 border-theme-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-theme-text mb-4">
          {editingBanner ? 'Edit Banner' : 'Create New Banner'}
        </h3>
        
        {/* Banner Preview */}
        {!editingBanner && selectedPlugin && (
          <div className="mb-6 p-4 bg-theme-background border-2 border-theme-border rounded-lg">
            <h3 className="text-lg font-semibold text-theme-text mb-3">Preview</h3>
            <p className="text-sm text-theme-text-muted mb-4">This is how your banner will appear on the plugin card</p>
            
            {bannerForm.banner_type === 'card' ? (
              /* Card Style Preview */
              <div className="border-2 rounded-lg p-4 bg-theme-surface relative" 
                   style={{
                     borderColor: bannerForm.color,
                     backgroundColor: hexToRgba(bannerForm.color, bannerForm.background_opacity),
                     borderStyle: bannerForm.border_style,
                     borderWidth: `${bannerForm.border_width}px`,
                     borderRadius: `${bannerForm.border_radius}px`,
                     minHeight: '200px'
                   }}>
                
                {/* Card Icon Preview */}
                {bannerForm.icon_enabled && bannerForm.icon && (
                  <div 
                    className={`absolute z-20 ${getCardIconPositionClass(bannerForm.card_icon_position, bannerForm.card_icon_placement)}`}
                    style={{
                      fontSize: `${bannerForm.icon_size}px`
                    }}
                  >
                    <img 
                      src={`https://unpkg.com/pixelarticons@1.8.0/svg/${bannerForm.icon}.svg`}
                      alt="card icon"
                      className="drop-shadow-lg"
                      style={{ 
                        filter: getColorFilter(bannerForm.color),
                        width: `${bannerForm.icon_size}px`,
                        height: `${bannerForm.icon_size}px`
                      }}
                    />
                  </div>
                )}

                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 rounded bg-theme-secondary flex items-center justify-center text-white font-bold">
                      P
                    </div>
                    <div>
                      <h4 className="font-bold text-theme-text">Plugin Name</h4>
                      <p className="text-sm text-theme-text-secondary">Plugin description goes here</p>
                    </div>
                  </div>
                  
                  {/* Card Banner Content */}
                  <div 
                    className={`inline-flex items-center gap-2 border-2 font-medium ${bannerForm.animation_enabled ? getAnimationClass(bannerForm.animation_type, bannerForm.animation_duration) : ''}`}
                    style={{
                      backgroundColor: hexToRgba(bannerForm.color, bannerForm.background_opacity),
                      borderColor: bannerForm.color,
                      color: bannerForm.color,
                      borderStyle: bannerForm.border_style,
                      borderWidth: `${bannerForm.border_width}px`,
                      borderRadius: `${bannerForm.border_radius}px`,
                      padding: `${bannerForm.padding}px`,
                      fontSize: `${bannerForm.font_size}px`,
                      fontWeight: bannerForm.font_weight,
                      margin: `${bannerForm.margin}px`,
                      animationDuration: bannerForm.animation_enabled ? `${bannerForm.animation_duration}ms` : undefined,
                      animationDelay: bannerForm.animation_enabled ? `${bannerForm.animation_delay}ms` : undefined
                    }}
                  >
                    {bannerForm.icon_enabled && bannerForm.icon && (
                      <img 
                        src={`https://unpkg.com/pixelarticons@1.8.0/svg/${bannerForm.icon}.svg`}
                        alt="icon"
                        style={{ 
                          filter: getColorFilter(bannerForm.color),
                          width: `${bannerForm.icon_size}px`,
                          height: `${bannerForm.icon_size}px`
                        }}
                      />
                    )}
                    <span className="font-semibold">{bannerForm.text}</span>
                  </div>
                </div>
              </div>
            ) : (
              /* Overlay Style Preview */
              <div className={`flex ${bannerForm.alignment === 'center' ? 'justify-center' : bannerForm.alignment === 'right' ? 'justify-end' : 'justify-start'}`}>
                <div 
                  className={`flex items-center gap-2 border-2 ${bannerForm.full_width ? 'w-full' : ''} ${bannerForm.animation_enabled ? getAnimationClass(bannerForm.animation_type, bannerForm.animation_duration) : ''}`}
                  style={{
                    backgroundColor: hexToRgba(bannerForm.color, bannerForm.background_opacity),
                    borderColor: bannerForm.color,
                    color: bannerForm.color,
                    borderStyle: bannerForm.border_style,
                    borderWidth: `${bannerForm.border_width}px`,
                    borderRadius: `${bannerForm.border_radius}px`,
                    padding: `${bannerForm.padding}px`,
                    fontSize: `${bannerForm.font_size}px`,
                    fontWeight: bannerForm.font_weight,
                    margin: `${bannerForm.margin}px`,
                    animationDuration: bannerForm.animation_enabled ? `${bannerForm.animation_duration}ms` : undefined,
                    animationDelay: bannerForm.animation_enabled ? `${bannerForm.animation_delay}ms` : undefined,
                    textAlign: bannerForm.overlay_text_align as any
                  }}
                >
                  {/* Render overlay banner content based on icon position */}
                  {bannerForm.overlay_icon_position === 'right' && (
                    <>
                      <span className="font-semibold">{bannerForm.text}</span>
                      {bannerForm.icon_enabled && bannerForm.icon && (
                        <img 
                          src={`https://unpkg.com/pixelarticons@1.8.0/svg/${bannerForm.icon}.svg`}
                          alt="icon"
                          style={{ 
                            filter: getColorFilter(bannerForm.color),
                            width: `${bannerForm.icon_size}px`,
                            height: `${bannerForm.icon_size}px`
                          }}
                        />
                      )}
                    </>
                  )}
                  
                  {bannerForm.overlay_icon_position === 'both' && (
                    <>
                      {bannerForm.icon_enabled && bannerForm.icon && (
                        <img 
                          src={`https://unpkg.com/pixelarticons@1.8.0/svg/${bannerForm.icon}.svg`}
                          alt="icon"
                          style={{ 
                            filter: getColorFilter(bannerForm.color),
                            width: `${bannerForm.icon_size}px`,
                            height: `${bannerForm.icon_size}px`
                          }}
                        />
                      )}
                      <span className="font-semibold">{bannerForm.text}</span>
                      {bannerForm.icon_enabled && bannerForm.icon && (
                        <img 
                          src={`https://unpkg.com/pixelarticons@1.8.0/svg/${bannerForm.icon}.svg`}
                          alt="icon"
                          style={{ 
                            filter: getColorFilter(bannerForm.color),
                            width: `${bannerForm.icon_size}px`,
                            height: `${bannerForm.icon_size}px`
                          }}
                        />
                      )}
                    </>
                  )}
                  
                  {bannerForm.overlay_icon_position.includes('stationary') && (
                    <div className="flex items-center justify-between w-full">
                      {bannerForm.overlay_icon_position === 'stationary-left' && (
                        <>
                          {bannerForm.icon_enabled && bannerForm.icon && (
                            <img 
                              src={`https://unpkg.com/pixelarticons@1.8.0/svg/${bannerForm.icon}.svg`}
                              alt="icon"
                              style={{ 
                                filter: getColorFilter(bannerForm.color),
                                width: `${bannerForm.icon_size}px`,
                                height: `${bannerForm.icon_size}px`
                              }}
                            />
                          )}
                          <span className="font-semibold">{bannerForm.text}</span>
                        </>
                      )}
                      
                      {bannerForm.overlay_icon_position === 'stationary-right' && (
                        <>
                          <span className="font-semibold">{bannerForm.text}</span>
                          {bannerForm.icon_enabled && bannerForm.icon && (
                            <img 
                              src={`https://unpkg.com/pixelarticons@1.8.0/svg/${bannerForm.icon}.svg`}
                              alt="icon"
                              style={{ 
                                filter: getColorFilter(bannerForm.color),
                                width: `${bannerForm.icon_size}px`,
                                height: `${bannerForm.icon_size}px`
                              }}
                            />
                          )}
                        </>
                      )}
                      
                      {bannerForm.overlay_icon_position === 'stationary-center' && (
                        <>
                          <div></div>
                          <div className="flex items-center gap-2">
                            {bannerForm.icon_enabled && bannerForm.icon && (
                              <img 
                                src={`https://unpkg.com/pixelarticons@1.8.0/svg/${bannerForm.icon}.svg`}
                                alt="icon"
                                style={{ 
                                  filter: getColorFilter(bannerForm.color),
                                  width: `${bannerForm.icon_size}px`,
                                  height: `${bannerForm.icon_size}px`
                                }}
                              />
                            )}
                            <span className="font-semibold">{bannerForm.text}</span>
                          </div>
                          <div></div>
                        </>
                      )}
                    </div>
                  )}
                  
                  {bannerForm.overlay_icon_position === 'left' && (
                    <>
                      {bannerForm.icon_enabled && bannerForm.icon && (
                        <img 
                          src={`https://unpkg.com/pixelarticons@1.8.0/svg/${bannerForm.icon}.svg`}
                          alt="icon"
                          style={{ 
                            filter: getColorFilter(bannerForm.color),
                            width: `${bannerForm.icon_size}px`,
                            height: `${bannerForm.icon_size}px`
                          }}
                        />
                      )}
                      <span className="font-semibold">{bannerForm.text}</span>
                    </>
                  )}
                </div>
              </div>
            )}
            <p className="text-xs text-theme-text-muted mt-2">
              Type: {bannerTypeOptions.find(t => t.value === bannerForm.banner_type)?.label} | 
              Position: {positionOptions.find(p => p.value === bannerForm.banner_position)?.label} | 
              Alignment: {alignmentOptions.find(a => a.value === bannerForm.alignment)?.label}
              {bannerForm.animation_enabled && ` | Animation: ${bannerForm.animation_type} (${bannerForm.animation_duration}ms)`}
              {bannerForm.icon_enabled && ` | Icon: ${bannerForm.icon} (${bannerForm.icon_size}px)`}
            </p>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex border-b border-theme-border mb-4">
          {[
            { id: 'basic', label: 'Basic', icon: 'edit' },
            { id: 'style', label: 'Style', icon: 'palette' },
            { id: 'animation', label: 'Animation', icon: 'animation' },
            { id: 'banner_position', label: 'Position', icon: 'layout' },
            { id: 'advanced', label: 'Advanced', icon: 'gear' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`px-4 py-2 font-medium transition-colors flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'text-theme-primary border-b-2 border-theme-primary'
                  : 'text-theme-text-secondary hover:text-theme-text'
              }`}
            >
              <img 
                src={`https://unpkg.com/pixelarticons@1.8.0/svg/${tab.icon}.svg`}
                alt={tab.label}
                className="w-4 h-4"
                style={{ filter: activeTab === tab.id ? 'brightness(0) saturate(100%) invert(40%) sepia(93%) saturate(1352%) hue-rotate(214deg) brightness(100%) contrast(97%)' : 'brightness(0) saturate(100%) invert(60%)' }}
              />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="space-y-4">
          {activeTab === 'basic' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {!editingBanner && (
                  <div>
                    <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                      Plugin
                    </label>
                    <select
                      value={selectedPlugin}
                      onChange={(e) => setSelectedPlugin(e.target.value)}
                      className="w-full p-3 bg-theme-surface-light border-2 border-theme-border rounded-md text-theme-text focus:border-theme-primary focus:outline-none"
                    >
                      <option value="">Select a plugin</option>
                      {pluginsLoading ? (
                        <option value="">Loading plugins...</option>
                      ) : (
                        plugins.map(plugin => (
                          <option key={plugin.id} value={plugin.id}>
                            {plugin.name}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                    Text
                  </label>
                  <input
                    type="text"
                    value={bannerForm.text}
                    onChange={(e) => setBannerForm(prev => ({ ...prev, text: e.target.value }))}
                    placeholder="Enter banner text"
                    className="w-full p-3 bg-theme-surface-light border-2 border-theme-border rounded-md text-theme-text focus:border-theme-primary focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                    Banner Type
                  </label>
                  <div className="grid grid-cols-1 gap-2">
                    {bannerTypeOptions.map(option => (
                      <button
                        key={option.value}
                        onClick={() => setBannerForm(prev => ({ ...prev, banner_type: option.value as any }))}
                        className={`p-3 border-2 rounded-md transition-all flex items-center gap-3 ${
                          bannerForm.banner_type === option.value
                            ? 'border-theme-primary bg-theme-primary/10 text-theme-primary'
                            : 'border-theme-border text-theme-text-secondary hover:border-theme-border-light'
                        }`}
                      >
                        <img 
                          src={`https://unpkg.com/pixelarticons@1.8.0/svg/${option.icon}.svg`}
                          alt={option.label}
                          className="w-5 h-5"
                          style={{ filter: bannerForm.banner_type === option.value ? 'brightness(0) saturate(100%) invert(40%) sepia(93%) saturate(1352%) hue-rotate(214deg) brightness(100%) contrast(97%)' : 'brightness(0) saturate(100%) invert(60%)' }}
                        />
                        <div className="text-left">
                          <div className="font-medium">{option.label}</div>
                          <div className="text-xs opacity-75">{option.description}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                    Color
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {availableColors.map(color => (
                      <button
                        key={color.value}
                        onClick={() => setBannerForm(prev => ({ ...prev, color: color.value }))}
                        className={`w-full h-10 rounded-md border-2 transition-all ${
                          bannerForm.color === color.value ? 'border-white' : 'border-transparent'
                        }`}
                        style={{ backgroundColor: color.value }}
                        title={color.name}
                      />
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                    Icon
                  </label>
                  <input
                    type="text"
                    placeholder="Search icons..."
                    value={iconSearch}
                    onChange={(e) => setIconSearch(e.target.value)}
                    className="w-full p-3 bg-theme-surface-light border-2 border-theme-border rounded-md text-theme-text focus:border-theme-primary focus:outline-none mb-4"
                  />
                  <div className="grid grid-cols-6 gap-2 max-h-48 overflow-y-auto">
                    {filteredIcons.map(icon => (
                      <button
                        key={icon}
                        onClick={() => setBannerForm(prev => ({ ...prev, icon: icon }))}
                        className={`w-full h-16 rounded-md border-2 transition-all flex flex-col items-center justify-center gap-1 ${
                          bannerForm.icon === icon
                            ? 'border-theme-primary bg-theme-primary/10 text-theme-primary'
                            : 'border-theme-border text-theme-text-secondary hover:border-theme-border-light'
                        }`}
                        title={icon}
                      >
                        <img 
                          src={`https://unpkg.com/pixelarticons@1.8.0/svg/${icon}.svg`}
                          alt={icon}
                          className="w-5 h-5"
                          style={{ filter: bannerForm.icon === icon ? 'brightness(0) saturate(100%) invert(40%) sepia(93%) saturate(1352%) hue-rotate(214deg) brightness(100%) contrast(97%)' : 'brightness(0) saturate(100%) invert(60%)' }}
                        />
                        <span className="text-xs truncate w-full text-center">{icon.replace(/[-_]/g, ' ')}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === 'style' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                  Border Style
                </label>
                <select
                  value={bannerForm.border_style}
                  onChange={(e) => setBannerForm(prev => ({ ...prev, border_style: e.target.value as any }))}
                  className="w-full p-3 bg-theme-surface-light border-2 border-theme-border rounded-md text-theme-text focus:border-theme-primary focus:outline-none"
                >
                  <option value="solid">Solid</option>
                  <option value="dashed">Dashed</option>
                  <option value="dotted">Dotted</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                  Border Width ({bannerForm.border_width}px)
                </label>
                <input
                  type="range"
                  min="1"
                  max="8"
                  value={bannerForm.border_width}
                  onChange={(e) => setBannerForm(prev => ({ ...prev, border_width: parseInt(e.target.value) }))}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                  Border Radius ({bannerForm.border_radius}px)
                </label>
                <input
                  type="range"
                  min="0"
                  max="24"
                  value={bannerForm.border_radius}
                  onChange={(e) => setBannerForm(prev => ({ ...prev, border_radius: parseInt(e.target.value) }))}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                  Padding ({bannerForm.padding}px)
                </label>
                <input
                  type="range"
                  min="4"
                  max="32"
                  value={bannerForm.padding}
                  onChange={(e) => setBannerForm(prev => ({ ...prev, padding: parseInt(e.target.value) }))}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                  Font Size ({bannerForm.font_size}px)
                </label>
                <input
                  type="range"
                  min="10"
                  max="24"
                  value={bannerForm.font_size}
                  onChange={(e) => setBannerForm(prev => ({ ...prev, font_size: parseInt(e.target.value) }))}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                  Font Weight
                </label>
                <select
                  value={bannerForm.font_weight}
                  onChange={(e) => setBannerForm(prev => ({ ...prev, font_weight: e.target.value as any }))}
                  className="w-full p-3 bg-theme-surface-light border-2 border-theme-border rounded-md text-theme-text focus:border-theme-primary focus:outline-none"
                >
                  <option value="normal">Normal</option>
                  <option value="medium">Medium</option>
                  <option value="semibold">Semibold</option>
                  <option value="bold">Bold</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                  Background Opacity ({(bannerForm.background_opacity * 100).toFixed(0)}%)
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={bannerForm.background_opacity}
                  onChange={(e) => setBannerForm(prev => ({ ...prev, background_opacity: parseFloat(e.target.value) }))}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                  Margin ({bannerForm.margin}px)
                </label>
                <input
                  type="range"
                  min="0"
                  max="32"
                  value={bannerForm.margin}
                  onChange={(e) => setBannerForm(prev => ({ ...prev, margin: parseInt(e.target.value) }))}
                  className="w-full"
                />
              </div>
            </div>
          )}

          {activeTab === 'animation' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="flex items-center gap-2 text-sm font-medium text-theme-text-secondary mb-4">
                  <input
                    type="checkbox"
                    checked={bannerForm.animation_enabled}
                    onChange={(e) => setBannerForm(prev => ({ ...prev, animation_enabled: e.target.checked }))}
                    className="w-4 h-4"
                  />
                  Enable Animation
                </label>
              </div>

              {bannerForm.animation_enabled && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                      Animation Type
                    </label>
                    <select
                      value={bannerForm.animation_type}
                      onChange={(e) => setBannerForm(prev => ({ ...prev, animation_type: e.target.value as any }))}
                      className="w-full p-3 bg-theme-surface-light border-2 border-theme-border rounded-md text-theme-text focus:border-theme-primary focus:outline-none"
                    >
                      {animationOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                      Duration ({bannerForm.animation_duration}ms)
                    </label>
                    <input
                      type="range"
                      min="500"
                      max="10000"
                      step="100"
                      value={bannerForm.animation_duration}
                      onChange={(e) => setBannerForm(prev => ({ ...prev, animation_duration: parseInt(e.target.value) }))}
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                      Delay ({bannerForm.animation_delay}ms)
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="5000"
                      step="100"
                      value={bannerForm.animation_delay}
                      onChange={(e) => setBannerForm(prev => ({ ...prev, animation_delay: parseInt(e.target.value) }))}
                      className="w-full"
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'banner_position' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                  Position
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {positionOptions.map(option => (
                    <button
                      key={option.value}
                      onClick={() => setBannerForm(prev => ({ ...prev, banner_position: option.value as any }))}
                      className={`p-3 border-2 rounded-md transition-all flex items-center gap-2 ${
                        bannerForm.banner_position === option.value
                          ? 'border-theme-primary bg-theme-primary/10 text-theme-primary'
                          : 'border-theme-border text-theme-text-secondary hover:border-theme-border-light'
                      }`}
                    >
                      <img 
                        src={`https://unpkg.com/pixelarticons@1.8.0/svg/${option.icon}.svg`}
                        alt={option.label}
                        className="w-4 h-4"
                        style={{ filter: bannerForm.banner_position === option.value ? 'brightness(0) saturate(100%) invert(40%) sepia(93%) saturate(1352%) hue-rotate(214deg) brightness(100%) contrast(97%)' : 'brightness(0) saturate(100%) invert(60%)' }}
                      />
                      <span className="text-sm font-medium">{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                  Alignment
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {alignmentOptions.map(option => (
                    <button
                      key={option.value}
                      onClick={() => setBannerForm(prev => ({ ...prev, alignment: option.value as any }))}
                      className={`p-3 border-2 rounded-md transition-all flex items-center justify-center gap-2 ${
                        bannerForm.alignment === option.value
                          ? 'border-theme-primary bg-theme-primary/10 text-theme-primary'
                          : 'border-theme-border text-theme-text-secondary hover:border-theme-border-light'
                      }`}
                    >
                      <img 
                        src={`https://unpkg.com/pixelarticons@1.8.0/svg/${option.icon}.svg`}
                        alt={option.label}
                        className="w-4 h-4"
                        style={{ filter: bannerForm.alignment === option.value ? 'brightness(0) saturate(100%) invert(40%) sepia(93%) saturate(1352%) hue-rotate(214deg) brightness(100%) contrast(97%)' : 'brightness(0) saturate(100%) invert(60%)' }}
                      />
                      <span className="text-sm font-medium">{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="flex items-center gap-2 text-sm font-medium text-theme-text-secondary">
                  <input
                    type="checkbox"
                    checked={bannerForm.full_width}
                    onChange={(e) => setBannerForm(prev => ({ ...prev, full_width: e.target.checked }))}
                    className="w-4 h-4"
                  />
                  Full Width (applies to header and footer positions)
                </label>
              </div>
            </div>
          )}

          {activeTab === 'advanced' && (
            <div className="space-y-6">
              {/* Priority and Icon Controls */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                    Priority ({bannerForm.priority})
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={bannerForm.priority}
                    onChange={(e) => setBannerForm(prev => ({ ...prev, priority: parseInt(e.target.value) }))}
                    className="w-full"
                  />
                  <p className="text-xs text-theme-text-muted mt-1">Higher priority banners appear first</p>
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-theme-text-secondary mb-2">
                    <input
                      type="checkbox"
                      checked={bannerForm.icon_enabled}
                      onChange={(e) => setBannerForm(prev => ({ ...prev, icon_enabled: e.target.checked }))}
                      className="w-4 h-4"
                    />
                    Show Icon
                  </label>
                </div>

                {bannerForm.icon_enabled && (
                  <div>
                    <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                      Icon Size ({bannerForm.icon_size}px)
                    </label>
                    <input
                      type="range"
                      min="8"
                      max="64"
                      value={bannerForm.icon_size}
                      onChange={(e) => setBannerForm(prev => ({ ...prev, icon_size: parseInt(e.target.value) }))}
                      className="w-full"
                    />
                  </div>
                )}
              </div>

              {/* Card Style Options */}
              {bannerForm.banner_type === 'card' && bannerForm.icon_enabled && (
                <div className="bg-theme-surface-light border border-theme-border rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-theme-text mb-4">Card Style Icon Options</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                        Icon Position
                      </label>
                      <div className="grid grid-cols-3 gap-1">
                        {cardIconPositionOptions.map(option => (
                          <button
                            key={option.value}
                            onClick={() => setBannerForm(prev => ({ ...prev, card_icon_position: option.value as any }))}
                            className={`p-2 border-2 rounded text-xs transition-all flex flex-col items-center gap-1 ${
                              bannerForm.card_icon_position === option.value
                                ? 'border-theme-primary bg-theme-primary/10 text-theme-primary'
                                : 'border-theme-border text-theme-text-secondary hover:border-theme-border-light'
                            }`}
                            title={option.label}
                          >
                            <img 
                              src={`https://unpkg.com/pixelarticons@1.8.0/svg/${option.icon}.svg`}
                              alt={option.label}
                              className="w-3 h-3"
                              style={{ filter: bannerForm.card_icon_position === option.value ? 'brightness(0) saturate(100%) invert(40%) sepia(93%) saturate(1352%) hue-rotate(214deg) brightness(100%) contrast(97%)' : 'brightness(0) saturate(100%) invert(60%)' }}
                            />
                            <span className="leading-none">{option.label.split(' ')[0]}<br/>{option.label.split(' ')[1]}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                        Icon Placement
                      </label>
                      <div className="space-y-2">
                        {cardIconPlacementOptions.map(option => (
                          <button
                            key={option.value}
                            onClick={() => setBannerForm(prev => ({ ...prev, card_icon_placement: option.value as any }))}
                            className={`w-full p-3 border-2 rounded-md transition-all flex items-center gap-3 ${
                              bannerForm.card_icon_placement === option.value
                                ? 'border-theme-primary bg-theme-primary/10 text-theme-primary'
                                : 'border-theme-border text-theme-text-secondary hover:border-theme-border-light'
                            }`}
                          >
                            <img 
                              src={`https://unpkg.com/pixelarticons@1.8.0/svg/${option.icon}.svg`}
                              alt={option.label}
                              className="w-4 h-4"
                              style={{ filter: bannerForm.card_icon_placement === option.value ? 'brightness(0) saturate(100%) invert(40%) sepia(93%) saturate(1352%) hue-rotate(214deg) brightness(100%) contrast(97%)' : 'brightness(0) saturate(100%) invert(60%)' }}
                            />
                            <div className="text-left">
                              <div className="text-sm font-medium">{option.label}</div>
                              <div className="text-xs opacity-75">{option.description}</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Overlay Banner Options */}
              {bannerForm.banner_type === 'overlay' && bannerForm.icon_enabled && (
                <div className="bg-theme-surface-light border border-theme-border rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-theme-text mb-4">Overlay Banner Options</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                        Text Alignment
                      </label>
                      <div className="grid grid-cols-3 gap-2">
                        {alignmentOptions.map(option => (
                          <button
                            key={option.value}
                            onClick={() => setBannerForm(prev => ({ ...prev, overlay_text_align: option.value as any }))}
                            className={`p-3 border-2 rounded-md transition-all flex items-center justify-center gap-2 ${
                              bannerForm.overlay_text_align === option.value
                                ? 'border-theme-primary bg-theme-primary/10 text-theme-primary'
                                : 'border-theme-border text-theme-text-secondary hover:border-theme-border-light'
                            }`}
                          >
                            <img 
                              src={`https://unpkg.com/pixelarticons@1.8.0/svg/${option.icon}.svg`}
                              alt={option.label}
                              className="w-4 h-4"
                              style={{ filter: bannerForm.overlay_text_align === option.value ? 'brightness(0) saturate(100%) invert(40%) sepia(93%) saturate(1352%) hue-rotate(214deg) brightness(100%) contrast(97%)' : 'brightness(0) saturate(100%) invert(60%)' }}
                            />
                            <span className="text-sm font-medium">{option.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                        Icon Position
                      </label>
                      <select
                        value={bannerForm.overlay_icon_position}
                        onChange={(e) => setBannerForm(prev => ({ ...prev, overlay_icon_position: e.target.value as any }))}
                        className="w-full p-3 bg-theme-surface border-2 border-theme-border rounded-md text-theme-text focus:border-theme-primary focus:outline-none"
                      >
                        <option value="left">Left</option>
                        <option value="right">Right</option>
                        <option value="both">Both Sides</option>
                        <option value="stationary-left">Stationary Left</option>
                        <option value="stationary-right">Stationary Right</option>
                        <option value="stationary-center">Stationary Center</option>
                      </select>
                      <p className="text-xs text-theme-text-muted mt-1">
                        Stationary positions create fixed layouts for text and icons
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex gap-2 mt-6">
          {editingBanner ? (
            <>
              <button
                onClick={handleUpdateBanner}
                disabled={!bannerForm.text.trim() || submitting}
                className="px-4 py-2 bg-theme-primary text-white rounded-md hover:bg-theme-secondary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Updating...' : 'Update Banner'}
              </button>
              <button
                onClick={() => {
                  setEditingBanner(null);
                  setBannerForm({
                    color: '#3b82f6',
                    text: '',
                    icon: 'info-circle',
                    banner_position: 'header' as 'header' | 'footer' | 'left-vertical' | 'right-vertical',
                    alignment: 'left' as 'left' | 'center' | 'right',
                    border_style: 'dashed' as 'solid' | 'dashed' | 'dotted',
                    border_width: 2,
                    border_radius: 8,
                    padding: 12,
                    font_size: 14,
                    font_weight: 'semibold' as 'normal' | 'medium' | 'semibold' | 'bold',
                    background_opacity: 0.15,
                    animation_enabled: false,
                    animation_type: 'none' as 'none' | 'pulse' | 'bounce' | 'fade' | 'slide' | 'glow' | 'shake' | 'rotate' | 'flip',
                    animation_duration: 2000,
                    animation_delay: 0,
                    full_width: false,
                    margin: 8,
                    banner_type: 'overlay' as 'overlay' | 'card',
                    // NEW ENHANCED FEATURES
                    priority: 1,
                    icon_enabled: true,
                    icon_size: 16,
                    card_icon_position: 'top-left' as 'top-left' | 'top-center' | 'top-right' | 'middle-left' | 'middle-center' | 'middle-right' | 'bottom-left' | 'bottom-center' | 'bottom-right',
                    card_icon_placement: 'overlay' as 'inside' | 'outside' | 'overlay',
                    overlay_text_align: 'left' as 'left' | 'center' | 'right',
                    overlay_icon_position: 'left' as 'left' | 'right' | 'both' | 'stationary-left' | 'stationary-right' | 'stationary-center'
                  });
                }}
                className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              onClick={handleCreateBanner}
              disabled={!selectedPlugin || !bannerForm.text.trim() || submitting}
              className="px-4 py-2 bg-theme-primary text-white rounded-md hover:bg-theme-secondary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Creating...' : 'Create Banner'}
            </button>
          )}
        </div>
      </div>

      {/* Existing Banners */}
      <div className="bg-theme-surface border-2 border-theme-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-theme-text mb-4">Existing Banners</h3>
        
        {banners.length === 0 ? (
          <p className="text-theme-text-muted">No banners created yet.</p>
        ) : (
          <div className="space-y-4">
            {banners.map(banner => {
              const plugin = plugins.find(p => p.id === banner.plugin_id);
              return (
                <div key={banner.id} className="flex items-center justify-between p-4 bg-theme-surface-light border border-theme-border rounded-lg">
                  <div className="flex items-center gap-4">
                    <div
                      className="inline-flex items-center gap-2 px-3 py-1 rounded-md text-sm font-medium border-2"
                      style={{ 
                        backgroundColor: `rgba(${parseInt(banner.color.slice(1, 3), 16)}, ${parseInt(banner.color.slice(3, 5), 16)}, ${parseInt(banner.color.slice(5, 7), 16)}, ${banner.background_opacity || 0.15})`,
                        borderColor: banner.color,
                        color: banner.color,
                        borderStyle: banner.border_style || 'dashed',
                        opacity: banner.enabled ? 1 : 0.5
                      }}
                    >
                      <img 
                        src={`https://unpkg.com/pixelarticons@1.8.0/svg/${banner.icon}.svg`}
                        alt="icon"
                        className="w-4 h-4"
                        style={{ filter: getColorFilter(banner.color) }}
                      />
                      {banner.text}
                    </div>
                    <div>
                      <p className="text-theme-text font-medium">{plugin?.name}</p>
                      <p className="text-sm text-theme-text-muted">
                        {banner.banner_position} | {banner.alignment} | {banner.enabled ? 'Enabled' : 'Disabled'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleBanner(banner.id)}
                      className={`px-3 py-1 text-sm rounded transition-colors ${
                        banner.enabled 
                          ? 'bg-yellow-500 text-white hover:bg-yellow-600' 
                          : 'bg-green-500 text-white hover:bg-green-600'
                      }`}
                    >
                      {banner.enabled ? 'Disable' : 'Enable'}
                    </button>
                    <button
                      onClick={() => handleEditBanner(banner)}
                      className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteBanner(banner.id)}
                      className="px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}; 